/**
 * App sidebar navigation component.
 *
 * Opinionated about its structure - knows which nav items exist.
 * Platform-agnostic for routing - uses navigation slots from context.
 *
 * Collapse state is pure CSS via data-collapsed attribute on root.
 * Child elements use data-sidebar-* attributes for CSS targeting.
 */

import * as stylex from "@stylexjs/stylex";
import { useState } from "react";

import type { Role } from "@animus/frontend-features/rbac";
import { home } from "@animus/iconic-svg/icons/home";
import { Icon, SidebarLinkMotion, sidebar } from "@animus/primaris";
import "@animus/primaris/sidebar-collapse.css";

import type { AppSidebarProps } from "./types";
import {
  type SidebarDestination,
  useNavigationSlots,
} from "../../capabilities/navigation";
import { AdminNavSection } from "../app-sidebar-admin";
import { ComposedSettingsMenu } from "../settings-menu";
import { AppSidebarHeader } from "./app-sidebar-header";

interface NavItemDef {
  destination: SidebarDestination;
  label: string;
  icon: string;
}

const MAIN_NAV_ITEMS: NavItemDef[] = [
  { destination: "home", label: "Home", icon: home },
];

function NavItem({
  item,
  isCollapsed,
}: {
  item: NavItemDef;
  isCollapsed: boolean;
}) {
  const { NavLink } = useNavigationSlots();

  return (
    <li>
      <NavLink destination={item.destination}>
        {({ isActive, navigate }) => (
          <SidebarLinkMotion
            data-sidebar-link
            isCollapsed={isCollapsed}
            {...stylex.props(
              sidebar.link,
              isActive && sidebar.linkActive,
              isActive && sidebar.linkActiveIndicator
            )}
            onClick={navigate}
            type="button"
          >
            <Icon
              data-sidebar-icon
              size="sm"
              svg={item.icon}
              xstyle={[sidebar.navIcon, isActive && sidebar.navIconActive]}
            />
            <span data-sidebar-label {...stylex.props(sidebar.linkLabel)}>
              {item.label}
            </span>
          </SidebarLinkMotion>
        )}
      </NavLink>
    </li>
  );
}

export function AppSidebar({
  user,
  onSignOut,
  onSignIn,
  variant = "default",
  isCollapsed: controlledCollapsed,
  onCollapsedChange: _onCollapsedChange,
  expandedSections: controlledExpandedSections,
  onSectionToggle,
  xstyle,
}: AppSidebarProps) {
  const [internalCollapsed] = useState(false);
  const [internalExpandedSections, setInternalExpandedSections] = useState<
    Set<string>
  >(new Set(["admin"]));

  const isCollapsed = controlledCollapsed ?? internalCollapsed;
  const expandedSections =
    controlledExpandedSections ?? internalExpandedSections;

  function handleSectionToggle(sectionKey: string) {
    if (onSectionToggle) {
      onSectionToggle(sectionKey);
    } else {
      setInternalExpandedSections((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(sectionKey)) {
          newSet.delete(sectionKey);
        } else {
          newSet.add(sectionKey);
        }
        return newSet;
      });
    }
  }

  const userRole = (user?.role as Role) ?? null;
  const isDefaultVariant = variant === "default";
  const isTransparentVariant = variant === "transparent";

  return (
    <nav
      data-collapsed={isCollapsed}
      data-sidebar-container
      {...stylex.props(
        sidebar.container,
        isDefaultVariant && sidebar.containerSurface,
        isTransparentVariant && sidebar.containerTransparent,
        xstyle
      )}
    >
      <AppSidebarHeader onSignIn={onSignIn} onSignOut={onSignOut} user={user} />

      <ul data-sidebar-nav {...stylex.props(sidebar.nav)}>
        {MAIN_NAV_ITEMS.map((item) => (
          <NavItem
            isCollapsed={isCollapsed}
            item={item}
            key={item.destination}
          />
        ))}

        <AdminNavSection
          isCollapsed={isCollapsed}
          isExpanded={expandedSections.has("admin")}
          onToggle={() => handleSectionToggle("admin")}
          userRole={userRole}
        />
      </ul>

      <div
        data-sidebar-footer
        data-sidebar-speed="2x"
        {...stylex.props(sidebar.footer)}
      >
        <ComposedSettingsMenu
          variant="nav"
          {...(user && onSignOut ? { onSignOut } : {})}
        />
      </div>
    </nav>
  );
}
